import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { toast } from "react-toastify";
import api from "utils/api";

function AddProduct() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    productName: "",
    subtitle: "",
    description: "",
    priceNormal: "",
    MRP: "",
    discountPrice: "",
    AvgRating: "",
    RatingCount: "",
  });
  const [imageBanner, setImageBanner] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setImageBanner(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.productName.trim()) {
      toast.error("Please enter product name");
      return;
    }
    if (!formData.priceNormal || formData.priceNormal <= 0) {
      toast.error("Please enter valid normal price");
      return;
    }
    if (formData.discountPrice === "" || parseFloat(formData.discountPrice) < 0) {
      toast.error("Please enter valid discount price");
      return;
    }
    if (parseFloat(formData.discountPrice) > parseFloat(formData.priceNormal)) {
      toast.error("Discount price cannot be greater than normal price");
      return;
    }
    if (!imageBanner) {
      toast.error("Please select product image");
      return;
    }

    setLoading(true);
    try {
      const data = new FormData();
      data.append("productName", formData.productName);
      data.append("subtitle", formData.subtitle);
      data.append("description", formData.description);
      data.append("priceNormal", formData.priceNormal);
      data.append("MRP", formData.MRP || 0);
      data.append("discountPrice", formData.discountPrice);
      data.append("AvgRating", formData.AvgRating || 0);
      data.append("RatingCount", formData.RatingCount || 0);
      data.append("imageBanner", imageBanner);

      const response = await api.post("/products", data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success(response.data.message || "Product created successfully");
        navigate("/product");
      } else {
        toast.error(response.data.message || "Failed to create product");
      }
    } catch (error) {
      console.error("Error creating product:", error);
      toast.error(error.response?.data?.message || "Failed to create product");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            <ArgonBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
              <ArgonTypography variant="h5" fontWeight="medium">
                Add New Product
              </ArgonTypography>
              <ArgonButton
                variant="outlined"
                color="secondary"
                size="small"
                onClick={() => navigate("/product")}
              >
                Back to List
              </ArgonButton>
            </ArgonBox>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Product Image Upload */}
                <Grid item xs={12}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Product Image *
                  </ArgonTypography>
                  <ArgonBox>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{
                        display: "block",
                        padding: "10px",
                        border: "1px solid #d2d6da",
                        borderRadius: "8px",
                        width: "100%",
                        cursor: "pointer",
                      }}
                    />
                  </ArgonBox>
                  {imagePreview && (
                    <ArgonBox mt={2}>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          maxWidth: "300px",
                          maxHeight: "200px",
                          borderRadius: "8px",
                          border: "1px solid #d2d6da",
                        }}
                      />
                    </ArgonBox>
                  )}
                </Grid>

                {/* Product Name */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Product Name *
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    name="productName"
                    value={formData.productName}
                    onChange={handleInputChange}
                    placeholder="Enter product name"
                  />
                </Grid>

                {/* Subtitle */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Subtitle
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    name="subtitle"
                    value={formData.subtitle}
                    onChange={handleInputChange}
                    placeholder="Enter subtitle"
                  />
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Description
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    multiline
                    rows={4}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter product description"
                  />
                </Grid>

                {/* Normal Price */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Normal Price (₹) *
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    type="number"
                    name="priceNormal"
                    value={formData.priceNormal}
                    onChange={handleInputChange}
                    placeholder="Enter normal price"
                  />
                </Grid>

                {/* MRP */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    MRP (₹)
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    type="number"
                    name="MRP"
                    value={formData.MRP}
                    onChange={handleInputChange}
                    placeholder="Enter MRP"
                  />
                </Grid>

                {/* Discount Price */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Discount Price (₹) *
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    type="number"
                    name="discountPrice"
                    value={formData.discountPrice}
                    onChange={handleInputChange}
                    placeholder="Enter discount price"
                  />
                </Grid>

                {/* Average Rating */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Average Rating (0-5)
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    type="number"
                    name="AvgRating"
                    value={formData.AvgRating}
                    onChange={handleInputChange}
                    placeholder="Enter average rating"
                    inputProps={{ min: 0, max: 5, step: 0.1 }}
                  />
                </Grid>

                {/* Rating Count */}
                <Grid item xs={12} md={6}>
                  <ArgonTypography variant="button" fontWeight="medium" mb={1}>
                    Rating Count
                  </ArgonTypography>
                  <ArgonInput
                    fullWidth
                    type="number"
                    name="RatingCount"
                    value={formData.RatingCount}
                    onChange={handleInputChange}
                    placeholder="Enter rating count"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>

              {/* Action Buttons */}
              <ArgonBox display="flex" justifyContent="flex-end" gap={2} mt={4}>
                <ArgonButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate("/product")}
                  disabled={loading}
                >
                  Cancel
                </ArgonButton>
                <ArgonButton type="submit" variant="gradient" color="info" disabled={loading}>
                  {loading ? "Creating..." : "Create Product"}
                </ArgonButton>
              </ArgonBox>
            </form>
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AddProduct;
